import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";
import { IPaginatedResponse } from "../models/Pagination";
import {
  ICreateQCResult,
  IPartNumberQCResult,
  IQCFilter,
  IQCRequest,
  IQCRequestDetail,
  IQCResult,
  IQCResultByRequest,
  IQCResultDetail,
  IReEvaluateQcResult,
} from "../models/Qc";
import { qcService } from "../services/qcService";

export function useCreateQcResult(): UseMutationResult<
  IPartNumberQCResult,
  Error,
  ICreateQCResult,
  unknown
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ICreateQCResult) => qcService.createQcResult(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["qc_request"],
        predicate: (query) => query.queryKey[0] === "qc_request",
      });
      queryClient.invalidateQueries({
        queryKey: ["part-ng"],
        predicate: (query) => query.queryKey[0] === "qc_request",
      });
      queryClient.invalidateQueries({
        queryKey: ["qc_request_detail"],
        predicate: (query) => query.queryKey[0] === "qc_request_detail",
      });
    },
  });
}
export function useGetQcResult(
  params: IQCFilter,
): UseQueryResult<IPaginatedResponse<IQCResult>> {
  return useQuery<IPaginatedResponse<IQCResult>>({
    queryKey: ["part-ng", params],
    queryFn: () => qcService.getQCResult(params),
  });
}
export function useGetQCRequestDetail(
  id: number,
): UseQueryResult<IQCRequestDetail> {
  return useQuery<IQCRequestDetail>({
    queryKey: ["qc_request_detail", id],
    queryFn: () => qcService.getQCRequestDetail(id),
    enabled: !!id,
  });
}
export function useGetQCRequest(
  params: IQCFilter,
): UseQueryResult<IPaginatedResponse<IQCRequest>> {
  return useQuery<IPaginatedResponse<IQCRequest>>({
    queryKey: ["qc_request", params],
    queryFn: () => qcService.getQCRequest(params),
  });
}
export function useGetQcResultDetail(
  id: number,
): UseQueryResult<IQCResultDetail> {
  return useQuery<IQCResultDetail>({
    queryKey: ["qc_result_detail", id],
    queryFn: () => qcService.getQCResultDetail(id),
    enabled: !!id,
  });
}
export function useEvaluateQCResult(): UseMutationResult<
  IQCResultDetail,
  unknown,
  { id: number; data: IReEvaluateQcResult },
  unknown
> {
  return useMutation({
    mutationFn: (body: { id: number; data: IReEvaluateQcResult }) =>
      qcService.reEvaluateQCResult(body.id, body.data),
  });
}
export function useAcceptQCRequest(): UseMutationResult<
  IQCRequestDetail,
  unknown,
  number,
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => qcService.acceptQCRequest(id),
    onSuccess: (data) => {
      queryClient.setQueryData<IQCRequestDetail>(
        ["qc_request_detail", data.id],
        data,
      );
      queryClient.invalidateQueries({
        queryKey: ["qc_request"],
      });
    },
  });
}

export function useBulkEvaluateQCRequest(): UseMutationResult<
  unknown,
  unknown,
  { requestId: number; file: File },
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ requestId, file }: { requestId: number; file: File }) =>
      qcService.bulkEvaluateQCRequest(requestId, file),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["qc_request_detail", variables.requestId],
      });
      queryClient.invalidateQueries({
        queryKey: ["qc_request"],
      });
      queryClient.invalidateQueries({
        queryKey: ["part-ng"],
      });
    },
  });
}

export function useGetQCResultByRequest(
  id: number,
): UseQueryResult<IQCResultByRequest[]> {
  return useQuery<IQCResultByRequest[]>({
    queryKey: ["qc_result_by_request", id],
    queryFn: () => qcService.getQCResultByRequestId(id),
  });
}
export function useCreateReturnPartNumber(
  requestId: number,
): UseMutationResult<number, unknown, number[], unknown> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (resultIds: number[]) =>
      qcService.createReturnPartNumber(resultIds),
    onSuccess: (_, resultIds) => {
      queryClient.setQueryData<IQCResultByRequest[]>(
        ["qc_result_by_request", requestId],
        (oldData) =>
          oldData?.map((item) => ({
            ...item,
            isReturn: item.isReturn || resultIds.includes(item.id),
          })),
      );
    },
  });
}
